import { get } from 'svelte/store';
import { formatDateTime } from '../../helpers'
import { routeLines, routeSources, routeTexts, routeTravelEstimates, loadAddresCoordinate, yards, yardMarkers, tripCalculations, activeRouteSchedule, quoteSelectedMaterial } from '../../stores'
import { mapbox } from '../mapbox.js'

/**
 * Mapbox access token injected globally via build environment.
 * @type {string}
 */
let pk = MAPBOX_TOKEN

/**
 * Cache storing active floating HTML duration badge markers on the map,
 * keyed by their route trailing ID.
 * @type {Map<string, mapboxgl.Marker>}
 */
const durationMarkers = new Map();

/**
 * Calculates a point along a line string at a given fractional ratio (default 0.90, i.e. 10% close to the destination yard).
 * Used to position duration badges comfortably close to the destination yard without overlapping the yard marker.
 *
 * @param {Array<[number, number]>} coordinates - Array of [lng, lat] coordinate pairs defining the path.
 * @param {number} [ratio=0.90] - Fractional distance along the line from start (0.0) to end (1.0). Default 0.90 is 10% away from destination.
 * @returns {[number, number]|null} The [lng, lat] coordinate at the specified ratio, or null if coordinates are empty.
 */
function getPointNearDestination(coordinates, ratio = 0.90) {
    if (!coordinates || coordinates.length === 0) return null;
    if (coordinates.length === 1) return coordinates[0];
    if (coordinates.length === 2) {
        return [
            coordinates[0][0] + (coordinates[1][0] - coordinates[0][0]) * ratio,
            coordinates[0][1] + (coordinates[1][1] - coordinates[0][1]) * ratio
        ];
    }

    let segmentLengths = [];
    let totalLength = 0;
    for (let i = 0; i < coordinates.length - 1; i++) {
        const p1 = coordinates[i];
        const p2 = coordinates[i + 1];
        const dx = (p2[0] - p1[0]) * Math.cos(((p1[1] + p2[1]) / 2) * Math.PI / 180);
        const dy = p2[1] - p1[1];
        const dist = Math.hypot(dx, dy);
        segmentLengths.push(dist);
        totalLength += dist;
    }

    if (totalLength === 0) return coordinates[coordinates.length - 1];

    const targetDist = totalLength * ratio;
    let accumulated = 0;

    for (let i = 0; i < segmentLengths.length; i++) {
        if (accumulated + segmentLengths[i] >= targetDist) {
            const segRatio = segmentLengths[i] === 0 ? 0 : (targetDist - accumulated) / segmentLengths[i];
            const p1 = coordinates[i];
            const p2 = coordinates[i + 1];
            return [
                p1[0] + (p2[0] - p1[0]) * segRatio,
                p1[1] + (p2[1] - p1[1]) * segRatio
            ];
        }
        accumulated += segmentLengths[i];
    }

    return coordinates[coordinates.length - 1];
}

/**
 * Normalizes duration text strings into standard display format for duration badges (e.g. "1 hr 15 min" or "20 min").
 *
 * @param {string|any} text - The input duration string or value.
 * @returns {string} Formatted duration string suitable for badge display.
 */
function formatDurationBadgeText(text) {
    if (!text) return "";
    if (typeof text !== "string") text = String(text);
    if (text.includes("min") || text.includes("hr")) return text;
    const match = text.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m)?$/i);
    if (match && (match[1] || match[2])) {
        const h = match[1];
        const m = match[2];
        if (h && m) return `${h} hr ${m} min`;
        if (h) return `${h} hr`;
        if (m) return `${m} min`;
    }
    return text;
}

/**
 * Plotted straight-line distance connections between the central load site and all disposal yard markers,
 * fetching duration estimates from the backend API and displaying lines with duration badges.
 *
 * @param {mapboxgl.Map} map - Active Mapbox GL map instance.
 * @param {[number, number]} centerCoord - [longitude, latitude] coordinates of the load site.
 * @param {Array<{id: string|number, marker: {_lngLat: {lng: number, lat: number}}}>} yardMarkers - Array of active yard markers on the map.
 * @returns {void}
 */
export function addDistanceLines(map, centerCoord, yardMarkers){
    
    // split the yardMarkets into groupd of 10 because the 
    // mapbox API only allows 10 coordinates per request
    //let allYardCoords = splitLocations(centerCoord, yardMarkers);
    let allYardCoords = generateCoordsForDuration(centerCoord, yardMarkers);
    let durationLines = getDurationLines(allYardCoords);

    durationLines.then(function (value) {
        let yardDurations = value.yard_durations;
    
        yardDurations.forEach(function(yardDuration, i){
                let lineText = "";
                lineText = secondsToHms(yardDuration.duration); 
                addLine(map, 
                        [[allYardCoords.load_coords.longitude, allYardCoords.load_coords.latitude], 
                        [yardDuration.yard_coords.longitude, yardDuration.yard_coords.latitude]], 
                        i.toString(), yardDuration.yard_id, null, null, lineText);
                routeTravelEstimates.update(current => ({
                    ...current,
                    [yardDuration.yard_id]: {
                        durationSeconds: yardDuration.duration,
                        durationMinutes: yardDuration.duration / 60
                    }
                }));
        });
    })

}

/**
 * Builds the coordinate payload required by the backend `/route/lines` duration endpoint.
 *
 * @param {[number, number]} centerCoord - [longitude, latitude] of the load site.
 * @param {Array<{id: string|number, marker: {_lngLat: {lng: number, lat: number}}}>} yardMarkers - Array of yard markers.
 * @returns {{load_coords: {latitude: number, longitude: number}, dispose_coords: Array<{latitude: number, longitude: number, yard_id: string|number}>}} Formatted coordinates object.
 */
function generateCoordsForDuration(centerCoord, yardMarkers){
    let yardCoords = {
        load_coords: {latitude: centerCoord[1], longitude: centerCoord[0]},
        dispose_coords: []
    };
    yardMarkers.forEach(function(element, i){
        try {
            yardCoords.dispose_coords.push({latitude: element.marker._lngLat.lat, longitude: element.marker._lngLat.lng, yard_id: element.id});
        } catch(err) {
            console.log(err)
        }
    });
    return yardCoords;
}

/**
 * Sends a POST request to the backend route lines API to retrieve calculated travel durations
 * between the load site and each disposal yard.
 *
 * @param {{load_coords: {latitude: number, longitude: number}, dispose_coords: Array<{latitude: number, longitude: number, yard_id: string|number}>}} allYardCoords - Formatted coordinate payload.
 * @returns {Promise<{yard_durations: Array<{yard_id: string|number, duration: number, yard_coords: {latitude: number, longitude: number}}>}>} API response containing duration per yard.
 * @throws {Error} If the API request fails or returns a non-OK status.
 */
async function getDurationLines(allYardCoords) {
    const headers = new Headers();
    headers.append("Content-Type", "application/json");
    
    const raw = JSON.stringify(allYardCoords)

    const requestOptions = {
        method: "POST",
        headers: headers,
        body: raw,
        redirect: "follow"
      };

    let url = TT_WEB_API_URL + "/route/lines"
    const res = await fetch(url, requestOptions);
    let durationLines = await res.json();

    if (res.ok) {
        return durationLines;
    } else {
        throw new Error(durationLines);
    }
}


/**
 * Adds a GeoJSON route line and an interactive Google Maps-style floating duration badge to the Mapbox map.
 * Safely removes existing layers/sources with matching ID before adding to prevent Mapbox collisions,
 * and tracks created IDs in Svelte stores.
 *
 * @param {mapboxgl.Map} map - Active Mapbox GL map instance.
 * @param {Array<[number, number]>} coordinates - Array of [lng, lat] coordinate pairs defining the route.
 * @param {string} trailingId - Unique suffix identifying this route line (e.g. index or yard ID).
 * @param {string|number} yardId - Unique identifier of the associated disposal yard.
 * @param {string} [lineColour='#888'] - Hex color code for the route line (default: grey '#888', active driving route: '#4285F4').
 * @param {number} [lineWidth=4] - Width of the route line in pixels.
 * @param {string} [lineText=""] - Duration text (e.g. "25 min") to display on the floating badge.
 * @returns {void}
 */
export function addLine(map, coordinates, trailingId, yardId, lineColour='#888', lineWidth=4, lineText="") {
    if (!map || !coordinates) return;
    if (!lineColour) lineColour = '#888';
    if (!lineWidth) lineWidth = 4;
    if (!lineText) lineText = "";
    
    const sourceId = 'routeSource' + trailingId;
    const lineId = 'routeLine' + trailingId;
    const textId = 'routeText' + trailingId;

    // Remove any existing layers/source with this ID first to prevent "already exists" errors
    if (map.getLayer(textId)) {
        map.removeLayer(textId);
    }
    if (map.getLayer(lineId)) {
        map.removeLayer(lineId);
    }
    if (map.getSource(sourceId)) {
        map.removeSource(sourceId);
    }

    map.addSource(
        sourceId, {
        'type': 'geojson',
        'data': {
            'type': 'Feature',
            'properties': {
                'yardId': yardId,
                'lineText': lineText
            },
            'geometry': {
                'type': 'LineString',
                'coordinates': coordinates
            },
            'yardId': yardId,
            'lineText': lineText
        }
    });

    // format line
    map.addLayer({
        'id': lineId,
        'type': 'line',
        'source': sourceId,
        'layout': {
            'line-join': 'round',
            'line-cap': 'round'
        },
        'paint': {
            'line-color': lineColour,
            'line-width': lineWidth
        }
    });

    // keep routeText symbol layer hidden for backwards compatibility
    map.addLayer({
        "id": textId,
        "type": "symbol",
        "source": sourceId,
        "layout": {
            "symbol-placement": "line",
            "text-font": ["Open Sans Regular"],
            "text-field": lineText,
            "text-size": 32,
            "visibility": "none"
        },
        "paint": {}
    });

    // Create Google Maps style floating duration badge on the route placed 10% close to the yard
    if (durationMarkers.has(trailingId)) {
        durationMarkers.get(trailingId).remove();
        durationMarkers.delete(trailingId);
    }

    if (lineText && coordinates && coordinates.length >= 2) {
        // Place badge at 0.90 ratio (90% from origin = 10% close to the yard)
        const badgeCoord = getPointNearDestination(coordinates, 0.90);
        if (badgeCoord) {
            const formattedText = formatDurationBadgeText(lineText);
            const isPrimary = (lineColour === '#4285F4');
            const timeColor = isPrimary ? '#188038' : '#5f6368';
            const iconColor = isPrimary ? '#5f6368' : '#70757a';

            const el = document.createElement('div');
            el.className = 'marker gmap-duration-marker';
            el.id = 'gmap-duration-' + trailingId;
            el.style.cursor = 'pointer';
            el.title = 'Click to view turnaround route schedule and load details';
            el.innerHTML = `
                <div class="gmap-duration-badge ${isPrimary ? 'primary' : 'alternate'}">
                    <svg class="gmap-car-icon" viewBox="0 0 24 24" width="12" height="12" fill="${iconColor}">
                        <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
                    </svg>
                    <span class="gmap-duration-time" style="color: ${timeColor};">${formattedText}</span>
                </div>
            `;

            el.addEventListener('click', (e) => {
                e.stopPropagation();
                const currentYards = get(yards) || [];
                const currentYard = currentYards.find(y => y.id === yardId);
                const currentCalcs = get(tripCalculations) || {};
                const calcLoad = (currentCalcs.loads || []).find(l => (
                    l.yard?.id === yardId ||
                    l.yard_id === yardId ||
                    l.yardId === yardId ||
                    (currentYard && (l.yard?.company?.name === currentYard.company?.name || l.yard?.name === currentYard.name))
                )) || currentYard?.load;
                const routes = calcLoad?.routes || currentYard?.load?.routes;

                showRouteSchedulePopup(map, badgeCoord, currentYard, calcLoad, routes);
            });

            const marker = new mapbox.Marker(el, { anchor: 'center' })
                .setLngLat(badgeCoord)
                .addTo(map);

            durationMarkers.set(trailingId, marker);
        }
    }

    routeSources.update(n => Array.from(new Set([...n, sourceId])));
    routeLines.update(n => Array.from(new Set([...n, lineId])));
    routeTexts.update(n => Array.from(new Set([...n, textId])));
}

/**
 * Tracks currently active route turnaround schedule Mapbox popup.
 * @type {mapboxgl.Popup|null}
 */
let currentSchedulePopup = null;

/**
 * Closes and destroys the active route schedule popup if open.
 * @returns {void}
 */
export function closeRouteSchedulePopup() {
    if (currentSchedulePopup) {
        try {
            currentSchedulePopup.remove();
        } catch (e) {}
        currentSchedulePopup = null;
    }
}

/**
 * Displays an interactive Mapbox popup anchored at specified coordinates showing the turnaround route schedule.
 *
 * @param {mapboxgl.Map} map - Active Mapbox GL map instance.
 * @param {[number, number]} coords - [lng, lat] coordinates to anchor the popup.
 * @param {Object} yard - Disposal yard object.
 * @param {Object} load - Calculation load object containing routes.
 * @param {Array<Object>} [routes] - Array of turnaround route leg objects.
 * @returns {void}
 */
export function showRouteSchedulePopup(map, coords, yard, load, routes) {
    if (!map || !coords) return;
    closeRouteSchedulePopup();

    const yardTitle = yard?.company?.name || yard?.name || load?.yard?.company?.name || load?.yard?.name || "Disposal Facility";
    const yardSuburb = yard?.address?.suburb || load?.yard?.address?.suburb || "";
    const currentMaterial = get(quoteSelectedMaterial) || "";
    const isMaterialAll = !currentMaterial || currentMaterial === "All";

    const yId = yard?.id || load?.yard?.id;

    if (!routes || routes.length === 0) {
        const noticeHtml = `
            <div class="tt-route-popup-card">
                <div class="tt-popup-header">
                    <div class="tt-popup-title">${yardTitle}</div>
                    ${yardSuburb ? `<span class="tt-popup-suburb">${yardSuburb}</span>` : ''}
                </div>
                <div class="tt-popup-body empty-notice" style="padding: 10px 0;">
                    <div style="font-size: 11.5px; color: #475569; line-height: 1.45;">
                        Turnaround route schedule (loads, durations, and tolls) is generated when haulage is calculated.
                    </div>
                    <div style="margin-top: 8px; font-size: 11px; color: #2563eb; font-weight: 600;">
                        Click <strong>Calculate Haulage</strong> in the planner to analyze this route.
                    </div>
                </div>
            </div>
        `;
        currentSchedulePopup = new mapbox.Popup({
            offset: 16,
            closeButton: true,
            closeOnClick: false,
            maxWidth: '340px',
            className: 'tt-route-schedule-mapbox-popup'
        })
        .setLngLat(coords)
        .setHTML(noticeHtml)
        .addTo(map);
        return;
    }

    const outboundLegs = routes.filter(r => r.direction === 'load to tip');
    const totalLoads = outboundLegs.length;
    const totalTollCents = routes.reduce((sum, r) => sum + (r.toll || 0), 0);
    const totalTollFormatted = (totalTollCents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
    const firstTime = routes[0]?.completed_at || "";
    const lastTime = routes[routes.length - 1]?.completed_at || "";

    let rowsHtml = '';
    routes.forEach((r, idx) => {
        const isOutbound = r.direction === 'load to tip';
        const dirLabel = isOutbound ? 'Site ➔ Tip' : 'Tip ➔ Site';
        const dirIcon = isOutbound ? '🚛' : '🔄';
        const hasTollError = !!r.toll_error;
        let tollText = 'No toll';
        let tollClass = 'is-free';
        let tollTitle = 'No road tolls on this route';
        if (hasTollError) {
            tollText = 'ERROR';
            tollClass = 'is-error';
            tollTitle = r.toll_error;
        } else if (r.toll > 0) {
            tollText = `$${(r.toll / 100).toFixed(2)}`;
            tollClass = 'has-toll';
            tollTitle = `Toll cost: ${tollText}`;
        }
        const loadTipText = isOutbound 
            ? `Load: ${r.duration_load || '—'} • Tip: ${r.duration_tip || r.duration_trip || '—'}` 
            : `Travel: ${r.duration_travel || '—'}`;

        rowsHtml += `
            <tr class="popup-leg-row ${isOutbound ? 'outbound-row' : 'inbound-row'}">
                <td class="td-p-leg">${idx + 1}</td>
                <td class="td-p-dir">
                    <span class="popup-dir-badge ${isOutbound ? 'outbound' : 'inbound'}">
                        <span>${dirIcon}</span>
                        <span>${dirLabel}</span>
                    </span>
                </td>
                <td class="td-p-time">${r.completed_at || '—'}</td>
                <td class="td-p-dur" title="${loadTipText}">
                    <strong>${r.duration_total || '—'}</strong>
                </td>
                <td class="td-p-toll ${tollClass}" title="${tollTitle}">${tollText}</td>
            </tr>
        `;
    });

    const popupHtml = `
        <div class="tt-route-popup-card">
            <div class="tt-popup-header">
                <div class="tt-popup-header-info">
                    <div class="tt-popup-title">${yardTitle}</div>
                    <div class="tt-popup-badges">
                        ${yardSuburb ? `<span class="tt-popup-suburb">${yardSuburb}</span>` : ''}
                        ${!isMaterialAll ? `<span class="tt-popup-material">${currentMaterial}</span>` : ''}
                    </div>
                </div>
            </div>

            <div class="tt-popup-kpi-bar">
                <div class="tt-kpi-pill">
                    <span class="tt-kpi-icon">🚛</span>
                    <span class="tt-kpi-text"><strong>${totalLoads}</strong> Loads</span>
                </div>
                <div class="tt-kpi-pill">
                    <span class="tt-kpi-icon">⏱️</span>
                    <span class="tt-kpi-text">${firstTime} – ${lastTime}</span>
                </div>
                <div class="tt-kpi-pill">
                    <span class="tt-kpi-icon">🛣️</span>
                    <span class="tt-kpi-text">${totalTollFormatted}</span>
                </div>
            </div>

            <div class="tt-popup-table-container">
                <table class="tt-popup-table">
                    <thead>
                        <tr>
                            <th class="th-p-leg">#</th>
                            <th class="th-p-dir">Direction</th>
                            <th class="th-p-time">Done</th>
                            <th class="th-p-dur">Leg Total</th>
                            <th class="th-p-toll">Toll</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml}
                    </tbody>
                </table>
            </div>

            <div class="tt-popup-footer">
                <button type="button" class="tt-popup-expand-btn" onclick="window.__openRouteScheduleModal && window.__openRouteScheduleModal(${yId})">
                    🔍 View Full Turnaround Breakdown
                </button>
            </div>
        </div>
    `;

    currentSchedulePopup = new mapbox.Popup({
        offset: 18,
        closeButton: true,
        closeOnClick: false,
        maxWidth: '430px',
        className: 'tt-route-schedule-mapbox-popup'
    })
    .setLngLat(coords)
    .setHTML(popupHtml)
    .addTo(map);
}

/**
 * Removes all route lines, symbol layers, sources, and floating duration markers from the map,
 * and clears the corresponding Svelte stores (`routeSources`, `routeLines`, `routeTexts`).
 *
 * @param {mapboxgl.Map} map - Active Mapbox GL map instance.
 * @returns {void}
 */
export function removeLines(map) {
    closeRouteSchedulePopup();
    durationMarkers.forEach(marker => marker.remove());
    durationMarkers.clear();

    let sources = get(routeSources);
    sources.forEach(function(sourceId) {
        removeLine(map, sourceId);
    });
    routeSources.set([]);
    routeLines.set([]);
    routeTexts.set([]);
    routeTravelEstimates.set({});
}

/**
 * Removes a specific route line, its symbol layer, source, and floating duration badge marker from the map.
 * Accepts either the trailing ID suffix (e.g. "0") or the full ID name (e.g. "routeLine0", "routeSource0").
 *
 * @param {mapboxgl.Map} map - Active Mapbox GL map instance.
 * @param {string} trailingId - Full identifier string or trailing identifier of the route to remove.
 * @param {boolean} [removeFromStores=false] - Whether to also splice out and remove this ID from the Svelte stores.
 * @returns {void}
 */
export function removeLine(map, trailingId, removeFromStores=false) {

    let index = 0
    let tempArray = []
    if (typeof trailingId === 'string') {
        trailingId = getTrailingIdFromString(trailingId)
    }

    if (durationMarkers.has(trailingId)) {
        durationMarkers.get(trailingId).remove();
        durationMarkers.delete(trailingId);
    }

    if (map.getLayer('routeLine' + trailingId)) {
        map.removeLayer('routeLine' + trailingId);
    }
    if(removeFromStores) {
        tempArray = get(routeLines) 
        index = tempArray.indexOf('routeLine' + trailingId)
        if (index > -1) {
            tempArray.splice(index, 1)
            routeLines.update(n => tempArray)
        }
    }

    if (map.getLayer('routeText' + trailingId)) {
        map.removeLayer('routeText' + trailingId);
    }
    if(removeFromStores) {
        tempArray = get(routeTexts) 
        index = tempArray.indexOf('routeText' + trailingId)
        if (index > -1) {
            tempArray.splice(index, 1)
            routeTexts.update(n => tempArray)
        }
    }

    if (map.getSource('routeSource' + trailingId)) {
        map.removeSource('routeSource' + trailingId);
    }
    if(removeFromStores) {
        tempArray = get(routeSources) 
        index = tempArray.indexOf('routeSource' + trailingId)
        if (index > -1) {
            tempArray.splice(index, 1)
            routeSources.update(n => tempArray)
        }
    }
}

/**
 * Extracts the trailing ID suffix from a full Mapbox layer or source identifier
 * by stripping 'routeLine', 'routeText', or 'routeSource' prefixes.
 *
 * @param {string} Id - Full identifier string (e.g. "routeSource0" or "routeLine12").
 * @returns {string} The isolated trailing ID suffix (e.g. "0" or "12").
 */
export function getTrailingIdFromString(Id) {
    let trailingId = Id
    trailingId = Id.replace('routeLine', '')
    trailingId = Id.replace('routeText', '')
    trailingId = Id.replace('routeSource', '')
    return trailingId
}

/**
 * Formats a duration in seconds into a human-readable string (e.g. "1 hr 15 min" or "25 min").
 *
 * @param {number|string} d - Duration in seconds.
 * @returns {string} Formatted duration string.
 */
function secondsToHms(d) {
    d = Number(d);
    var h = Math.floor(d / 3600);
    var m = Math.round((d % 3600) / 60);

    if (h > 0) {
        return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
    }
    return `${Math.max(1, m)} min`;
}


/**
 * Fetches and plots actual driving road routes from the Mapbox Directions API for all filtered yards
 * starting from the load site location.
 * Renders actual road geometry in blue (#4285F4) and displays the travel time duration badge
 * on the route placed 10% close to each destination yard.
 *
 * @param {mapboxgl.Map} map - Active Mapbox GL map instance.
 * @param {Array<object>} [targetYards=null] - Optional list of filtered yards to map. Defaults to $yards (or $yardMarkers).
 * @param {[number, number]} [centerCoord=null] - Optional origin [lng, lat]. Defaults to $loadAddresCoordinate.
 * @returns {Promise<number>} Resolves with the count of successfully mapped routes.
 */
export async function mapDirections(map, targetYards = null, centerCoord = null) {
    if (!map) return 0;

    // Clear any previous lines and duration badges before drawing actual routes
    removeLines(map);

    const originCoord = centerCoord || get(loadAddresCoordinate);
    if (!originCoord || !Array.isArray(originCoord) || originCoord.length < 2) {
        console.warn("mapDirections: No valid load site coordinate available.");
        return 0;
    }

    const origin = `${originCoord[0]},${originCoord[1]}`;

    // Determine filtered yards list
    let yardsList = targetYards;
    if (!yardsList || yardsList.length === 0) {
        const storeYards = get(yards);
        if (storeYards && storeYards.length > 0) {
            yardsList = storeYards;
        } else {
            yardsList = get(yardMarkers);
        }
    }

    if (!yardsList || yardsList.length === 0) {
        console.warn("mapDirections: No filtered yards available to map.");
        return 0;
    }

    const activeYardMarkers = get(yardMarkers);

    // Calculate next Monday date for traffic estimation
    let d = new Date();
    d.setDate(d.getDate() + (1 + 7 - d.getDay()) % 7);
    let dateString = formatDateTime(d);

    let mappedCount = 0;

    routeTravelEstimates.set({});

    // Process each filtered yard sequentially
    for (let i = 0; i < yardsList.length; i++) {
        const yard = yardsList[i];
        const yardId = yard.id;
        const trailingId = yardId != null ? yardId.toString() : i.toString();

        // Resolve destination coordinates [destLng, destLat]
        let destLng = null;
        let destLat = null;

        if (yard.address && yard.address.coordinates) {
            destLng = yard.address.coordinates.lng ?? yard.address.coordinates.longitude;
            destLat = yard.address.coordinates.lat ?? yard.address.coordinates.latitude;
        } else if (yard.marker) {
            const pos = typeof yard.marker.getLngLat === 'function' ? yard.marker.getLngLat() : yard.marker._lngLat;
            if (pos) {
                destLng = pos.lng;
                destLat = pos.lat;
            }
        }

        // Fallback: check active yardMarkers on the map
        if (destLng == null && activeYardMarkers && activeYardMarkers.length > 0) {
            const markerObj = activeYardMarkers.find(m => m.id === yardId);
            if (markerObj && markerObj.marker) {
                const pos = typeof markerObj.marker.getLngLat === 'function' ? markerObj.marker.getLngLat() : markerObj.marker._lngLat;
                if (pos) {
                    destLng = pos.lng;
                    destLat = pos.lat;
                }
            }
        }

        if (destLng == null || destLat == null) {
            console.warn("mapDirections: Could not resolve coordinates for yard:", yardId);
            continue;
        }

        const destination = `${destLng},${destLat}`;

        const url = "https://api.mapbox.com/directions/v5/mapbox/driving/" + origin + ";" + destination + 
                "?alternatives=false&geometries=geojson&overview=simplified&steps=false" + 
                "&notifications=none&depart_at=" + dateString + "&access_token=" + pk;

        try {
            const res = await fetch(url);
            const direction = await res.json();
            if (direction && direction.routes && direction.routes[0]) {
                const route = direction.routes[0];
                const durationText = secondsToHms(route.duration);
                // Draw actual driving route polyline in blue (#4285F4) with travel time badge 10% from the destination yard
                addLine(map, route.geometry.coordinates, trailingId, yardId, '#4285F4', 4, durationText);
                routeTravelEstimates.update(current => ({
                    ...current,
                    [yardId]: {
                        durationSeconds: route.duration,
                        durationMinutes: route.duration / 60,
                        distanceMeters: route.distance,
                        geometry: route.geometry
                    }
                }));
                mappedCount++;
            }
        } catch (err) {
            console.error("Failed to map directions for yard:", yardId, err);
        }
    }

    return mappedCount;
}